// AdminGovernancePage — /admin/governance — DAO management for admins.
// Backend enforces is_admin on every endpoint; this UI just surfaces it.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Download, XOctagon, Wrench } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const fmt = (n) => Number(n).toLocaleString();
const TYPES = [
  "course_add", "course_remove", "feature_request", "vault_params",
  "burn_rate", "mentor_admit", "partnership", "treasury", "other",
];

export default function AdminGovernancePage() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", type: "course_add", duration_days: 7 });
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState(null);

  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const load = useCallback(() => {
    if (!token) return;
    fetch(`${API}/governance/admin/overview`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 403 ? "Solo admins" : "Error cargando");
        setData(await r.json());
      })
      .catch((e) => setError(e.message));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const createOfficial = async () => {
    setCreating(true); setMsg(null);
    try {
      const r = await fetch(`${API}/governance/admin/proposals`, {
        method: "POST", credentials: "include", headers, body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(JSON.stringify(d?.detail || d));
      setMsg("✅ Propuesta oficial creada");
      setForm({ title: "", description: "", type: "course_add", duration_days: 7 });
      load();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const closeEarly = async (id) => {
    const reason = window.prompt("Motivo del cierre anticipado:");
    if (!reason) return;
    const r = await fetch(`${API}/governance/admin/close/${id}`, {
      method: "POST", credentials: "include", headers, body: JSON.stringify({ reason }),
    });
    const d = await r.json().catch(() => ({}));
    setMsg(r.ok ? `✅ Cerrada → ${d.result}` : `❌ ${JSON.stringify(d?.detail || d)}`);
    load();
  };

  const markImplemented = async (id) => {
    const r = await fetch(`${API}/governance/admin/implement/${id}`, {
      method: "POST", credentials: "include", headers,
    });
    const d = await r.json().catch(() => ({}));
    setMsg(r.ok ? "✅ Marcada como implementada" : `❌ ${JSON.stringify(d?.detail || d)}`);
    load();
  };

  const exportCsv = async (id) => {
    const r = await fetch(`${API}/governance/admin/export/${id}`, {
      credentials: "include", headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { setMsg("❌ Export falló"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `governance-votes-${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return null;
  if (error) return <div className="min-h-screen bg-[#05060B] pt-32 text-center text-red-400">{error}</div>;

  const s = data?.stats;
  return (
    <div className="min-h-screen bg-[#05060B] text-white" data-testid="admin-governance-page">
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/governance" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-white/40 hover:text-purple-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Governance
        </Link>
        <h1 className="mt-4 text-3xl font-black">Admin · Gobernanza RE₿EL</h1>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ["Propuestas", s ? fmt(s.totalProposals) : "…"],
            ["Activas", s ? fmt(s.activeProposals) : "…"],
            ["Aprobadas", s ? fmt(s.approvedProposals) : "…"],
            ["R₿L votados", s ? fmt(Number(s.totalVotesCast)) : "…"],
          ].map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{l}</p>
              <p className="mt-1 font-mono text-xl font-black">{v}</p>
            </div>
          ))}
        </div>

        {/* Create official proposal */}
        <div className="mt-8 rounded-2xl border border-purple-400/30 bg-purple-400/[0.05] p-5">
          <h2 className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-purple-300">
            Crear propuesta oficial del equipo
          </h2>
          <input value={form.title} maxLength={100} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Título" data-testid="admin-gov-title"
            className="mt-3 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm font-bold text-white outline-none placeholder:text-white/25 focus:border-purple-400/60" />
          <textarea value={form.description} maxLength={2000} rows={4} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descripción completa" data-testid="admin-gov-description"
            className="mt-2 w-full resize-y rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white outline-none placeholder:text-white/25 focus:border-purple-400/60" />
          <div className="mt-2 flex flex-wrap gap-3">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="admin-gov-type"
              className="rounded-xl border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white outline-none">
              {TYPES.map((ty) => <option key={ty} value={ty} className="bg-[#0B0F19]">{ty}</option>)}
            </select>
            <select value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })} data-testid="admin-gov-duration"
              className="rounded-xl border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white outline-none">
              {[3, 7, 14].map((d) => <option key={d} value={d} className="bg-[#0B0F19]">{d} días</option>)}
            </select>
            <button onClick={createOfficial} disabled={creating || form.title.length < 8 || form.description.length < 20}
              data-testid="admin-gov-create-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-400 to-fuchsia-500 px-6 py-2.5 text-xs font-black uppercase tracking-[0.18em] text-black disabled:opacity-40">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publicar
            </button>
          </div>
        </div>

        {msg && <p className="mt-3 font-mono text-xs text-white/70" data-testid="admin-gov-msg">{msg}</p>}

        {/* Proposals table */}
        <h2 className="mt-10 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">
          Todas las propuestas ({(data?.proposals || []).length})
        </h2>
        <div className="mt-3 space-y-3">
          {(data?.proposals || []).map((p) => (
            <div key={p.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`admin-gov-row-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-black uppercase ${
                  p.status === "active" ? "bg-cyan-400/15 text-cyan-300"
                    : p.result === "passed" ? "bg-emerald-400/15 text-emerald-300"
                    : "bg-rose-400/15 text-rose-300"
                }`}>{p.status}</span>
                {p.implemented && <span className="rounded-full bg-amber-400/15 px-2 py-0.5 font-mono text-[9px] font-black uppercase text-amber-300">implementada</span>}
                {p.is_official && <span className="rounded-full bg-purple-400/15 px-2 py-0.5 font-mono text-[9px] font-black uppercase text-purple-300">oficial</span>}
                <span className="ml-auto font-mono text-[10px] text-white/35">{(p.ends_at || "").slice(0, 16).replace("T", " ")} UTC</span>
              </div>
              <p className="mt-2 text-sm font-bold text-white">{p.title}</p>
              <p className="mt-1 font-mono text-[11px] text-white/50">
                SÍ {fmt(p.yes_votes)} ({p.yes_pct}%) · NO {fmt(p.no_votes)} ({p.no_pct}%) ·
                {" "}{fmt(p.total_voters)} votantes · quórum {p.quorum_pct}%
                {p.close_reason && <span className="ml-2 text-amber-300">cierre: {p.close_reason}</span>}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.status === "active" && (
                  <button onClick={() => closeEarly(p.id)} data-testid={`admin-gov-close-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/40 bg-rose-400/10 px-3 py-1.5 text-[10px] font-black uppercase text-rose-300 hover:bg-rose-400/20">
                    <XOctagon className="h-3 w-3" /> Cerrar
                  </button>
                )}
                {p.result === "passed" && !p.implemented && (
                  <button onClick={() => markImplemented(p.id)} data-testid={`admin-gov-implement-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-[10px] font-black uppercase text-amber-300 hover:bg-amber-400/20">
                    <Wrench className="h-3 w-3" /> Implementada
                  </button>
                )}
                <button onClick={() => exportCsv(p.id)} data-testid={`admin-gov-export-${p.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-[10px] font-black uppercase text-white/70 hover:bg-white/10">
                  <Download className="h-3 w-3" /> CSV
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
